import { IconChevronLeftFilled } from '@zillow/constellation-icons/react/filled/icon-chevron-left-filled';
import { IconChevronRightFilled } from '@zillow/constellation-icons/react/filled/icon-chevron-right-filled';
import { useMemo } from 'react';

import { IconButton } from '../../icon-button/icon-button';
import type { IconButtonPropsInterface } from '../../icon-button/types/icon-button.types';
import type {
  UseDateTimeBlockRenderPaginationButtonPropsInterface,
  UseDateTimeBlockRenderPaginationPropsInterface,
  UseDateTimeBlockRenderPaginationReturnInterface,
} from '../types/use-render-pagination.types';

/*
 * Hoisted constants — these never change across renders, so referencing
 * them from the memo dep arrays below keeps the returned JSX elements
 * referentially stable when inputs are unchanged.
 */
const SHARED_BUTTON_PROPS: Omit<IconButtonPropsInterface, 'title' | 'disabled' | 'onClick'> = {
  emphasis: 'bare',
  shape: 'circle',
  size: 'xs',
  tone: 'neutral',
};

const PREV_ICON = <IconChevronLeftFilled />;
const NEXT_ICON = <IconChevronRightFilled />;

/**
 * Hook for rendering pagination navigation buttons with controlled behavior
 *
 * @param props - Configuration for the pagination buttons
 * @returns Rendered navigation button components
 */
export function useDateTimeBlockRenderPagination(
  props: UseDateTimeBlockRenderPaginationPropsInterface,
): UseDateTimeBlockRenderPaginationReturnInterface {
  const {
    renderDatesPrevIconButton,
    renderDatesNextIconButton,
    isPrevDisabled,
    isNextDisabled,
    handlePrevious,
    handleNext,
  } = props;

  return useMemo(() => {
    const buttonPrevProps: UseDateTimeBlockRenderPaginationButtonPropsInterface = {
      disabled: isPrevDisabled,
      onClick: handlePrevious,
    };
    const buttonNextProps: UseDateTimeBlockRenderPaginationButtonPropsInterface = {
      disabled: isNextDisabled,
      onClick: handleNext,
    };

    const prevIconButtonElement = renderDatesPrevIconButton ? (
      renderDatesPrevIconButton(buttonPrevProps)
    ) : (
      <IconButton
        icon={PREV_ICON}
        title="Previous set of dates"
        {...SHARED_BUTTON_PROPS}
        {...buttonPrevProps}
      />
    );

    const nextIconButtonElement = renderDatesNextIconButton ? (
      renderDatesNextIconButton(buttonNextProps)
    ) : (
      <IconButton
        icon={NEXT_ICON}
        title="Next set of dates"
        {...SHARED_BUTTON_PROPS}
        {...buttonNextProps}
      />
    );

    return {
      prevIconButtonElement,
      nextIconButtonElement,
    };
  }, [
    handleNext,
    handlePrevious,
    isNextDisabled,
    isPrevDisabled,
    renderDatesNextIconButton,
    renderDatesPrevIconButton,
  ]);
}
